import React, { useState, useMemo } from 'react';
// FIX: Decouple from AppProvider and use props for callbacks. Import useAuth from its own provider.
import { useAuth } from '../contexts/AuthProvider';
import CloseIcon from './icons/CloseIcon';
import SearchIcon from './icons/SearchIcon';
import type { User } from '../types';

interface NewMessageModalProps {
    onClose: () => void;
    // FIX: Add onSelectUser prop to make component reusable and fix type error.
    onSelectUser: (user: User) => void;
}

const NewMessageModal: React.FC<NewMessageModalProps> = ({ onClose, onSelectUser }) => {
    const { users, currentUser } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');

    const filteredUsers = useMemo(() => {
        if (!searchTerm.trim()) return [];
        const lowercasedTerm = searchTerm.toLowerCase();
        return users.filter(user =>
            user.handle !== currentUser?.handle && // Exclude self
            (user.name.toLowerCase().includes(lowercasedTerm) ||
             user.handle.toLowerCase().includes(lowercasedTerm))
        );
    }, [searchTerm, users, currentUser]);

    return (
        <div 
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/60"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div 
                className="bg-brand-surface rounded-lg shadow-xl w-full max-w-md m-4 flex flex-col max-h-[70vh]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
                    <h2 className="text-lg font-bold text-white">New Message</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-700" aria-label="Close modal">
                        <CloseIcon className="h-6 w-6 text-white" />
                    </button>
                </div>
                
                <div className="p-4 border-b border-gray-700/50">
                    <div className="relative">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search for a user..."
                            className="w-full bg-gray-700 text-white rounded-md p-2 pl-8 focus:outline-none"
                            autoFocus
                        />
                        <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {searchTerm.trim() && filteredUsers.length === 0 && (
                        <p className="text-center text-brand-muted p-8">No users found.</p>
                    )}
                    <ul className="divide-y divide-gray-700/50">
                        {filteredUsers.map(user => (
                            <li key={user.handle}>
                                <button 
                                    onClick={() => onSelectUser(user)}
                                    className="w-full flex items-center space-x-4 p-4 hover:bg-gray-700/50 transition-colors"
                                >
                                    <img src={user.avatarUrl} alt={user.name} className="h-10 w-10 rounded-full" />
                                    <div className="text-left">
                                        <p className="font-bold text-white">{user.name}</p>
                                        <p className="text-sm text-brand-muted">@{user.handle}</p>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default NewMessageModal;